import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mentorsAPI } from '../services/api';
import { MasterclassCard } from '../components/Card';
import Avatar from '../components/Avatar';
import './ListPage.css';
import './MentorDetail.css';

const AIS_DIMS = [
  { key: 'regularite',           label: 'Régularité',             weight: '35 %', max: 350,
    detail: (m) => { const n = m.session_count || 0; return `${n} session${n > 1 ? 's' : ''} données (repère : 150)`; } },
  { key: 'validation_pairs',     label: 'Validation pair-à-pair', weight: '30 %', max: 300,
    detail: (m) => m.review_count > 0 ? `Note moyenne ${(m.rating || 0).toFixed(1)}/5` : "Pas encore d'avis" },
  { key: 'impact_generationnel', label: 'Impact générationnel',   weight: '20 %', max: 200,
    detail: (m) => { const n = m.review_count || 0; return `${n} avis reçu${n > 1 ? 's' : ''} (repère : 40)`; } },
  { key: 'reciprocite',          label: 'Réciprocité',            weight: '15 %', max: 150,
    detail: (m) => m.reciprocity_score > 0 ? 'A lui-même reçu du mentorat sur la plateforme' : "N'a pas encore reçu de mentorat" },
];

export default function MentorDetail() {
  const { id } = useParams();
  const [mentor, setMentor]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    mentorsAPI.show(id)
      .then(({ data }) => { if (!cancelled) setMentor(data.data); })
      .catch(() => { if (!cancelled) setError('Mentor introuvable.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="loading" style={{ padding: '3rem 0' }}>Chargement du profil...</div>;

  if (error || !mentor) {
    return (
      <div className="list-page">
        <div className="empty">{error || 'Mentor introuvable.'}</div>
        <Link to="/mentors" className="back-link">← Retour aux mentors</Link>
      </div>
    );
  }

  const user  = mentor.user || {};
  const ais   = mentor.alumni_impact_score;
  const bd = mentor.ais_breakdown || {};
  const aisBreakdown = AIS_DIMS.map((d) => ({ ...d, points: bd[d.key] ?? 0, detail: d.detail(mentor) }));

  return (
    <div className="mentor-detail-page">
      <Link to="/mentors" className="back-link">← Retour aux mentors</Link>

      <div className="mentor-detail-header">
        <Avatar user={user} size="lg" className="mentor-detail-avatar" />
        <div>
          <h1>{user.full_name}</h1>
          <p className="mentor-detail-role">{mentor.title}{mentor.company ? ` · ${mentor.company}` : ''}</p>
          <p className="mentor-detail-meta">
            {(user.wilaya || mentor.location) && <span>📍 {user.wilaya || mentor.location}</span>}
            {mentor.is_verified && <span className="badge verified">Vérifié</span>}
            {mentor.is_available && <span className="badge available">Disponible pour du mentorat</span>}
          </p>
          {(mentor.domains || []).length > 0 && (
            <div className="card-tags">
              {mentor.domains.map((d) => <span key={d} className="tag">{d}</span>)}
            </div>
          )}
        </div>
      </div>

      {mentor.bio && (
        <section className="detail-section">
          <h2>À propos</h2>
          <p className="mentor-detail-bio">{mentor.bio}</p>
        </section>
      )}

      <section className="detail-section">
        <div className="ais-section-head">
          <h2>Alumni Impact Score™</h2>
          {ais > 0 ? (
            <span className="ais-badge ais-badge--active ais-badge--lg">
              AIS™ <strong>{ais}</strong><span className="ais-denom">/1000</span>
            </span>
          ) : (
            <span className="ais-badge ais-badge--empty ais-badge--lg">AIS™ — en construction</span>
          )}
        </div>
        <p className="ais-intro">
          Score composite calculé sur 4 dimensions pondérées, qui reflètent la contribution réelle du mentor à l'écosystème alumni.
        </p>
        <div className="ais-breakdown">
          {aisBreakdown.map((dim) => (
            <div key={dim.key} className="ais-dim">
              <div className="ais-dim-head">
                <span className="ais-dim-label">{dim.label} <span className="ais-dim-weight">({dim.weight})</span></span>
                <span className="ais-dim-points">{dim.points} <span className="ais-denom">/ {dim.max}</span></span>
              </div>
              <div className="ais-dim-bar">
                <div className="ais-dim-bar-fill" style={{ width: `${Math.min((dim.points / dim.max) * 100, 100)}%` }} />
              </div>
              <p className="ais-dim-detail">{dim.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {mentor.masterclasses && mentor.masterclasses.length > 0 && (
        <section className="detail-section">
          <h2>Masterclasses proposées</h2>
          <div className="grid-3">
            {mentor.masterclasses.map((mc) => (
              <MasterclassCard key={mc.id} item={{ ...mc, mentor }} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
